import * as React from "react";

function isStandalonePwa() {
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return window.matchMedia("(display-mode: standalone)").matches || nav.standalone === true;
}

/**
 * Mobile document lock + iOS standalone PWA viewport compensation.
 *
 * WebKit #254868: installed PWAs with viewport-fit=cover report 100dvh / inset:0
 * as (physical screen − bottom safe-area). That blank band under the UI is what
 * Chrome device mode never shows. Shell CSS uses
 * `calc(100dvh + env(safe-area-inset-bottom))`; this hook keeps `app-standalone`
 * in sync and refreshes after lock/unlock.
 */
export function useAppViewportHeight(enabled: boolean) {
  React.useEffect(() => {
    const root = document.documentElement;

    if (!enabled) {
      root.classList.remove("app-mobile-shell");
      root.classList.remove("app-standalone");
      return;
    }

    root.classList.add("app-mobile-shell");

    const syncStandalone = () => {
      if (isStandalonePwa()) root.classList.add("app-standalone");
      else root.classList.remove("app-standalone");
    };

    const refresh = () => {
      if (document.visibilityState === "hidden") return;
      window.scrollTo(0, 0);
      syncStandalone();
    };

    syncStandalone();

    const onVisibility = () => {
      refresh();
      window.setTimeout(refresh, 50);
      window.setTimeout(refresh, 300);
      window.setTimeout(refresh, 600);
    };

    window.addEventListener("orientationchange", onVisibility);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pageshow", onVisibility);

    return () => {
      window.removeEventListener("orientationchange", onVisibility);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pageshow", onVisibility);
      root.classList.remove("app-mobile-shell");
      root.classList.remove("app-standalone");
    };
  }, [enabled]);
}
